import { getAllThemeIds } from '@unifyapps/carbon/theme/utils/themeConditionalUtils';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { useMemo } from 'react';
import { useThemeEntitiesByIds } from './useThemeEntitiesByIds';

/**
 * Fetches all interface themes (default + conditional), lazily from runner side effects —
 * only the default theme is needed promptly, and useGetInterfaceThemeById handles that.
 * Endpoint selection (public/private, pre/post login) lives in useThemeEntitiesByIds.
 */
export function useInterfaceThemes(interfaceRecord?: InterfaceEntity) {
  const { defaultThemeIdList, restThemeIds } = useMemo(() => {
    // getAllThemeIds puts the default id first; split so the default-theme query body is
    // identical to useGetInterfaceThemeById's → react-query serves both from one request,
    // and the rest query never re-downloads the default theme.
    const [defaultThemeId, ...remainingThemeIds] = getAllThemeIds(
      interfaceRecord?.properties.theme?.defaultThemeId,
    );
    return {
      defaultThemeIdList: defaultThemeId ? [defaultThemeId] : [],
      restThemeIds: remainingThemeIds,
    };
  }, [interfaceRecord?.properties.theme?.defaultThemeId]);

  const defaultThemeResult = useThemeEntitiesByIds(defaultThemeIdList);
  const restThemesResult = useThemeEntitiesByIds(restThemeIds);

  const themes = useMemo(
    () => [...defaultThemeResult.themes, ...restThemesResult.themes],
    [defaultThemeResult.themes, restThemesResult.themes],
  );

  return {
    themes,
    isLoading: defaultThemeResult.isLoading || restThemesResult.isLoading,
    error: defaultThemeResult.error ?? restThemesResult.error,
  };
}
